<script>
import { mapGetters } from 'vuex';
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
// NOTICE: KAPTION-INTEGRATION - Client-managed assistant assignment permission.
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

export default {
  components: {
    Avatar,
    Spinner,
    NextButton,
  },
  props: {
    selectedInboxes: {
      type: Array,
      default: () => [],
    },
    conversationCount: {
      type: Number,
      default: 0,
    },
  },
  emits: ['select', 'close'],
  setup() {
    // NOTICE: KAPTION-INTEGRATION
    const { canUseFeature } = useFeaturePermissions();
    return { canUseFeature };
  },
  data() {
    return {
      query: '',
      selectedAssistant: null,
      goBackToAssistantList: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'bulkActions/getUIFlags',
      // NOTICE: KAPTION-INTEGRATION
      assignableAssistantsUiFlags: 'inboxAssignableAssistants/getUIFlags',
    }),
    filteredAssistants() {
      if (this.query) {
        return this.assignableAssistants.filter(assistant =>
          assistant.name.toLowerCase().includes(this.query.toLowerCase())
        );
      }
      return [
        {
          confirmed: true,
          name: 'None',
          id: null,
          role: 'assistant',
          account_id: 0,
          email: 'None',
        },
        ...this.assignableAssistants,
      ];
    },
    assignableAssistants() {
      return this.$store.getters[
        'inboxAssignableAssistants/getAssignableAssistants'
      ](this.selectedInboxes.join(','));
    },
    conversationLabel() {
      return this.conversationCount > 1 ? 'conversations' : 'conversation';
    },
  },
  mounted() {
    // NOTICE: KAPTION-INTEGRATION - Do not fetch a list the user cannot use.
    if (!this.canUseFeature('assign_ai_assistant')) return;

    this.$store.dispatch(
      'inboxAssignableAssistants/fetch',
      this.selectedInboxes
    );
  },
  methods: {
    submit() {
      this.$emit('select', this.selectedAssistant);
    },
    goBack() {
      this.goBackToAssistantList = true;
      this.selectedAssistant = null;
    },
    assignAssistant(assistant) {
      this.selectedAssistant = assistant;
    },
    onClose() {
      this.$emit('close');
    },
    onCloseAssistantList() {
      if (this.selectedAssistant === null && !this.goBackToAssistantList) {
        this.onClose();
      }
      this.goBackToAssistantList = false;
    },
  },
};
</script>

<template>
  <!-- eslint-disable-next-line vue/no-root-v-if -->
  <div
    v-if="canUseFeature('assign_ai_assistant')"
    v-on-clickaway="onCloseAssistantList"
    class="bulk-action__assistants"
  >
    <div class="triangle">
      <svg height="12" viewBox="0 0 24 12" width="24">
        <path d="M20 12l-8-8-12 12" fill-rule="evenodd" stroke-width="1px" />
      </svg>
    </div>
    <div class="flex items-center justify-between header">
      <span>{{ $t('BULK_ACTION.ASSISTANT_SELECT_LABEL') }}</span>
      <NextButton ghost xs slate icon="i-lucide-x" @click="onClose" />
    </div>
    <div class="container">
      <div
        v-if="assignableAssistantsUiFlags.isFetching"
        class="assistant__list-loading"
      >
        <Spinner />
        <p>{{ $t('BULK_ACTION.ASSISTANT_LIST_LOADING') }}</p>
      </div>
      <div v-else class="assistant__list-container">
        <ul v-if="!selectedAssistant">
          <li class="search-container">
            <div
              class="flex items-center justify-between h-8 gap-2 assistant-list-search"
            >
              <fluent-icon icon="search" class="search-icon" size="16" />
              <input
                v-model="query"
                type="search"
                :placeholder="$t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
                class="reset-base !outline-0 !text-sm assistant--search_input"
              />
            </div>
          </li>
          <li v-for="assistant in filteredAssistants" :key="assistant.id">
            <div
              class="assistant-list-item"
              @click="assignAssistant(assistant)"
            >
              <Avatar
                :name="assistant.name"
                :src="assistant.thumbnail"
                :status="assistant.availability_status"
                :size="22"
                hide-offline-status
                rounded-full
              />
              <span class="my-0 text-n-slate-12">
                {{ assistant.name }}
              </span>
            </div>
          </li>
        </ul>
        <div v-else class="assistant-confirmation-container">
          <p v-if="selectedAssistant.id">
            {{
              $t('BULK_ACTION.ASSISTANT_ASSIGN_CONFIRMATION_LABEL', {
                conversationCount,
                conversationLabel,
              })
            }}
            <strong>
              {{ selectedAssistant.name }}
            </strong>
            <span>?</span>
          </p>
          <p v-else>
            {{
              $t('BULK_ACTION.UNASSIGN_CONFIRMATION_LABEL', {
                conversationCount,
                conversationLabel,
              })
            }}
          </p>
          <div class="assistant-confirmation-actions">
            <NextButton
              faded
              sm
              slate
              type="reset"
              :label="$t('BULK_ACTION.GO_BACK_LABEL')"
              @click="goBack"
            />
            <NextButton
              sm
              type="submit"
              :label="$t('BULK_ACTION.YES')"
              :is-loading="uiFlags.isUpdating"
              @click="submit"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bulk-action__assistants {
  @apply max-w-[75%] absolute ltr:right-2 rtl:left-2 top-12 origin-top-right w-auto z-20 min-w-[15rem] bg-n-alpha-3 backdrop-blur-[100px] border-n-weak rounded-lg border border-solid shadow-md;
  .header {
    @apply p-2.5;

    span {
      @apply text-sm font-medium;
    }
  }

  .container {
    @apply overflow-y-auto max-h-[15rem];
    .assistant__list-container {
      @apply h-full;
    }
    .assistant-list-search {
      @apply py-0 px-2.5 bg-n-alpha-black2 border border-solid border-n-strong rounded-md;
      .search-icon {
        @apply text-n-slate-10;
      }

      .assistant--search_input {
        @apply border-0 text-xs m-0 dark:bg-transparent bg-transparent h-[unset] w-full;
      }
    }
  }
  .triangle {
    @apply block z-10 absolute -top-3 text-left ltr:right-[--triangle-position] rtl:left-[--triangle-position];

    svg path {
      @apply fill-n-alpha-3 backdrop-blur-[100px]  stroke-n-weak;
    }
  }
}
ul {
  @apply m-0 list-none;

  li {
    &:last-child {
      .assistant-list-item {
        @apply last:rounded-b-lg;
      }
    }
  }
}

.assistant-list-item {
  @apply flex items-center p-2.5 gap-2 cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-solid-3;
  span {
    @apply text-sm;
  }
}

.assistant-confirmation-container {
  @apply flex flex-col h-full p-2.5;
  p {
    @apply flex-grow;
  }
  .assistant-confirmation-actions {
    @apply w-full grid grid-cols-2 gap-2.5;
  }
}
.search-container {
  @apply py-0 px-2.5 sticky top-0 z-20 bg-n-alpha-3 backdrop-blur-[100px];
}

.assistant__list-loading {
  @apply m-2.5 rounded-md dark:bg-n-solid-3 bg-n-slate-2 flex items-center justify-center flex-col p-5 h-[calc(95%-6.25rem)];
}
</style>
